import { useSyncExternalStore } from 'react';
import { docViewerKind, type DocViewerKind } from './doc-viewer';
import type { WorkspaceFile } from './workspace-files';
import { effectiveContent } from './file-record';

/**
 * The document viewer's open state lives HERE — in one module-level presenter — and NOT in whichever
 * component the user clicked. This is the web port of native's `DocumentViewerPresenter`
 * (`ios/.../Features/Misc/DocumentViewer.swift`, `android/.../features/DocumentViewer.kt`), which is a
 * singleton state holder with the viewer hosted once at the app root (`RootView.swift`'s overlay chain /
 * `AppRoot.kt`'s ZStack). Same shape, same reason.
 *
 * The reason is a real defect, not symmetry for its own sake. The open state used to be a `useState`
 * inside `SentAttachmentList`, which renders inside a chat message row — so the viewer's lifetime was the
 * ROW's lifetime. An optimistic just-sent message re-keys when it reconciles to its server id, and that
 * unmounted an open report mid-read. A presenter plus one root-level host decouples the viewer from every
 * row, panel and screen that can open it: whoever opened it may unmount freely.
 *
 * Read it in React with `useSyncExternalStore` rather than a context provider — the same module-level-store
 * precedent as `lib/modal-escape.ts` and `lib/unread-notifications-store.ts`. A provider would have to be
 * threaded through `MessageRow`'s four `SentAttachmentList` call sites, and through the non-framed pages,
 * to say something no component owns anyway.
 */
export interface DocViewerRequest {
  file: WorkspaceFile;
  /** Which viewer to render, resolved BY THE CALLER — see `DocViewerModal`'s `kind` prop. A caller has the
   *  attachment's `mimeType`; a `WorkspaceFile` does not, so re-deriving downstream loses the MIME rescue.
   *
   *  `null` is a first-class value: the file opens in the SAME modal, which says it cannot be previewed and
   *  offers Download / Open in new tab / Discuss. Every file type therefore has one presentation — before
   *  this, a `null` kind fell out of the viewer entirely and each caller improvised (navigate away, or
   *  `window.open` a tab), so "what happens when I click a file" depended on which list you clicked from. */
  kind: DocViewerKind | null;
  /** The BI page_name the viewer's own actions report under. */
  pageName: string;
  /** This file's own page as a new-tab URL (`fileNewTabLink`), when the OPENER can name one.
   *
   *  Only the caller knows: a Files row has the workspace slug, a chat attachment has none — a chat file is
   *  identified by (conversation, message, name), and no route of ours carries that. So a chat attachment
   *  leaves this absent and `buildFileActions` tiers "Open in new tab" down to the file's own signed URL,
   *  which is also exactly what the chip beside it already does. Inventing a `?url=<signed>` route instead
   *  would put the signed URL in the address bar and history — the very thing this replaced — and cost a new
   *  `PAGE_NAMES` entry plus two native mirrors for a screen neither phone has. */
  fileHref?: string;
  /** The workspace the file belongs to, for the viewer header's subtitle. Only the surfaces that know it
   *  (the workspace gallery) pass it; absent it, the subtitle omits the segment rather than fabricating it. */
  workspaceName?: string;
}

/** What the host reads. `id` distinguishes two successive opens of the same file so the host can re-key
 *  (and therefore reset the modal's query/scroll state) instead of silently reusing the old instance. */
export interface OpenDocViewer extends DocViewerRequest {
  id: number;
}

let current: OpenDocViewer | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

const emit = (): void => {
  for (const listener of [...listeners]) listener();
};

/** Presents the document viewer at the root. Replaces whatever was open. */
export function openDocViewer(request: DocViewerRequest): void {
  current = { ...request, id: nextId++ };
  emit();
}

/** What "clicking a file row" does: classify it and open the viewer — for EVERY kind, including `null`.
 *
 *  It used to open only the viewable kinds and hand `null` back so each caller could improvise, which is how
 *  one product ended up with three answers to "what happens when I click a file": the gallery navigated to
 *  another page, the cross-workspace list called `window.open`, and a card did something else again. The
 *  modal is now the single presentation and a `null` kind is a state INSIDE it.
 *
 *  Still returns the kind, because callers legitimately care (a row uses it to decide whether a plain click
 *  should `preventDefault` its own href). */
export function openFileForRow(
  item: WorkspaceFile,
  pageName: string,
  fileHref?: string,
  workspaceName?: string,
): DocViewerKind | null {
  const kind = docViewerKind(item.name, effectiveContent(item));
  openDocViewer({ file: item, kind, pageName, fileHref, workspaceName });
  return kind;
}

/** Dismisses the viewer. A no-op (and no notification) when nothing is open. */
export function dismissDocViewer(): void {
  if (current == null) return;
  current = null;
  emit();
}

/** The open request, or `null`. Stable identity between changes, as `useSyncExternalStore` requires. */
export const currentDocViewer = (): OpenDocViewer | null => current;

/** SSR/hydration snapshot: nothing is ever open on the server. */
const serverDocViewer = (): OpenDocViewer | null => null;

export function subscribeDocViewer(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React binding for the presenter — used by the single root-level host. */
export function useDocViewer(): OpenDocViewer | null {
  return useSyncExternalStore(subscribeDocViewer, currentDocViewer, serverDocViewer);
}

/** Test seam: how many hosts/readers are subscribed. */
export const docViewerListenerCount = (): number => listeners.size;

/** Test seam: drop any open request without notifying, so one test can't leak into the next. */
export function resetDocViewerForTest(): void {
  current = null;
  listeners.clear();
}
